// Meniul lateral al platformei — replică bara din stânga de la scalpx.ru.
//
// Colapsat arată doar iconițe (64px); la hover se desfășoară și apar etichetele.
// Butonul de pin îl fixează deschis, ca la ei.

import { el, clear, bus } from '../core/util.js';
import { icon } from './icons.js';
import { settings } from '../core/state.js';
import { session, initials } from '../core/session.js';
import { pareMobil, versiuneaAleasa } from '../core/device.js';
import { pictAvatar } from './avatars.js';
import { openAuth } from './auth-modal.js';
import { toast } from './components.js';

const PIN_KEY = 'sx_sidebar_pinned';

/** Structura de meniu, cu aceleași iconițe Font Awesome ca la ScalpX. */
const GROUPS = [
  {
    title: 'Инструменты',
    items: [
      { id: 'screener', label: 'Скринер', icon: 'chartLine', href: '/screener/' },
      { id: 'preplay', label: 'Тренажёр', icon: 'clockHistory', href: '/preplay/' },
    ],
  },
  {
    title: 'Основное',
    items: [
      // Profilul şi lenta sus: într-o reţea socială astea sunt lucrurile pentru care
      // intri, iar tabloul de bord e ce te uiţi din când în când.
      { id: 'feed', label: 'Лента', icon: 'comments', href: '/feed/' },
      { id: 'profile', label: 'Мой профиль', icon: 'user', href: '/profile/' },
      { id: 'members', label: 'Участники', icon: 'users', href: '/members/' },
      { id: 'messages', label: 'Сообщения', icon: 'send', href: '/messages/', badge: true },
      { id: 'dashboard', label: 'Дашборд', icon: 'dashboard', href: '/' },
      { id: 'menu', label: 'Главное меню', icon: 'rocket', href: '/menu' },
      { id: 'ideas', label: 'Торговые идеи', icon: 'lightbulb', href: '/publications/' },
      { id: 'reports', label: 'Торговые отчёты', icon: 'clipboard', href: '/reports/' },
      { id: 'chat', label: 'Общий чат', icon: 'comments', href: '/chat/' },
      { id: 'voice', label: 'Голосовой чат', icon: 'headset', href: '/voice', locked: true },
      { id: 'leaderboard', label: 'Лидеры рейтинга', icon: 'trophy', href: '/leaderboard/' },
    ],
  },
  {
    title: 'Обучение',
    items: [
      { id: 'strategies', label: 'Стратегии', icon: 'book', href: '/strategies/' },
      { id: 'education', label: 'Видео уроки', icon: 'play', href: '/education', locked: true },
      { id: 'streams', label: 'Стримы', icon: 'broadcast', href: '/streams', locked: true },
    ],
  },
  {
    title: 'Аккаунт',
    items: [
      { id: 'account', label: 'Настройки аккаунта', icon: 'userGear', href: '/account/' },
      // Culoarea aplicatiei nu e o setare de screener, cum era pana acum: e a
      // aplicatiei. Se deschide de aici, dintr-o apasare, nu de dupa patru.
      { id: 'tema', label: 'Тема оформления', icon: 'palette', actiune: 'tema' },
      // Drumul inapoi. „Полная версия" era o usa cu sens unic: odata trecuta de pe
      // telefon, nu mai aveai de unde te intoarce, fiindca meniul de jos — singurul
      // loc unde statea comutatorul — dispare in versiunea mare.
      { id: 'mobil', label: 'Мобильная версия', icon: 'window', actiune: 'mobil', doarPeTelefon: true },
      // Moderarea e alt lucru decat directorul de participanti: acolo te uiti la
      // oameni, aici le schimbi rolul si ii blochezi. O vede doar personalul.
      { id: 'admin', label: 'Модерация', icon: 'shieldCheck', href: '/admin/', staff: true },
      { id: 'achievements', label: 'Достижения', icon: 'medal', href: '/achievements', locked: true },
      { id: 'about', label: 'О платформе', icon: 'info', href: '/about/' },
      { id: 'updates', label: 'Обновления', icon: 'rocket', href: '/updates' },
      { id: 'rules', label: 'Правила', icon: 'book', href: '/rules/' },
      { id: 'support', label: 'Поддержка', icon: 'lifeRing', href: '/support/' },
    ],
  },
];

export class Sidebar {
  constructor(app) {
    this.app = app;
    this.pinned = settings.get(PIN_KEY, false);
    this.links = new Map();
  }

  mount(host) {
    this.root = el('aside', { class: `sidebar${this.pinned ? ' pinned' : ''}` });

    this.root.append(
      el('div', { class: 'sb-brand' }, [
        el('span', { class: 'sb-brand-mark' }, [
          el('span', { class: 'sb-brand-s', text: 'S' }),
          el('span', { class: 'sb-brand-x', text: 'X' }),
        ]),
        el('button', {
          class: 'sb-pin',
          title: this.pinned ? 'Открепить меню' : 'Закрепить меню',
          onclick: () => this.togglePin(),
        }, [icon(this.pinned ? 'anglesLeft' : 'anglesRight', 12)]),
      ]),
    );

    this.navEl = el('div', { class: 'sb-nav' });
    this.#umpleNav();
    this.root.append(this.navEl);

    // Blocul de utilizator stă jos, ca la ei: e ultimul lucru din meniu și primul
    // la care te uiți ca să vezi cine ești și cum ieși.
    this.footerEl = el('div', { class: 'sb-footer' });
    this.root.append(this.footerEl);
    this.#renderUser();
    this.offSession = bus.on('session:change', () => { this.#renderUser(); this.#umpleNav(); });

    host.append(this.root);
    this.setActive(location.pathname);
    return this;
  }

  /**
   * Randurile de meniu.
   *
   * Se reface la intrare si la iesire, fiindca „Модерация" apare doar pentru personal:
   * daca s-ar construi o singura data, randul ar ramane pe ecran de la cel de dinainte.
   */
  #umpleNav() {
    clear(this.navEl);
    this.links.clear();
    for (const group of GROUPS) {
      const randuri = group.items.filter((it) => {
        if (it.staff && !['owner', 'admin'].includes(session.user?.role)) return false;
        // Randul de intoarcere are rost pe un aparat care chiar e telefon — si oriunde
        // cineva a cerut anume versiunea completa. Al doilea caz conteaza cel mai mult:
        // e singura cale inapoi dupa o alegere facuta din meniul de jos, care in
        // versiunea mare nu mai exista.
        if (it.doarPeTelefon && !pareMobil() && versiuneaAleasa() !== 'desktop') return false;
        return true;
      });
      if (!randuri.length) continue;
      this.navEl.append(el('div', { class: 'sb-group-title', text: group.title }));
      for (const item of randuri) {
        const link = el('a', {
          class: `sb-item${item.locked ? ' locked' : ''}`,
          href: item.href || '#',
          title: item.locked ? 'Требуется активная подписка' : item.label,
          onclick: async (event) => {
            event.preventDefault();
            if (item.locked) { this.app.showLocked(item.label); return; }
            if (item.actiune === 'tema') {
              (await import('./theme-picker.js')).deschideTeme();
              return;
            }
            if (item.actiune === 'mobil') {
              (await import('../core/device.js')).alegeVersiune('mobil');
              return;
            }
            this.app.navigate(item.href);
          },
        }, [
          el('span', { class: 'sb-item-icon' }, [icon(item.icon, 15)]),
          el('span', { class: 'sb-item-text', text: item.label }),
          item.badge ? (this.badgeEl = el('span', { class: 'sb-item-badge', text: '0' })) : null,
          item.locked ? el('span', { class: 'sb-item-lock' }, [icon('lock', 9)]) : null,
        ]);
        if (item.href) this.links.set(item.href, link);
        this.navEl.append(link);
      }
    }
    this.setActive(location.pathname);
  }

  #renderUser() {
    clear(this.footerEl);
    if (!session.authenticated) {
      this.footerEl.append(el('button', {
        class: 'sb-login', title: 'Войти в сообщество',
        onclick: () => openAuth(),
      }, [
        el('span', { class: 'sb-item-icon' }, [icon('user', 15)]),
        el('span', { class: 'sb-item-text', text: 'Войти' }),
      ]));
      return;
    }

    const u = session.user;
    this.footerEl.append(el('div', { class: 'sb-user' }, [
      el('button', {
        class: 'sb-user-main', title: `${u.username} — ${u.status.name}`,
        onclick: () => this.app.navigate(`/profile/${u.username}/`),
      }, [
        // Acelaşi avatar ca pe profil: dacă omul şi-a ales unul, îl vede peste tot,
        // nu doar în locul unde l-a ales.
        (() => {
          const av = el('span', { class: 'sb-user-avatar', style: { background: u.status.color } });
          pictAvatar(av, u, 30);
          return av;
        })(),
        el('span', { class: 'sb-user-txt' }, [
          el('span', { class: 'sb-user-nick', text: u.username, style: { color: u.status.color } }),
          el('span', { class: 'sb-user-rank', text: `${u.status.name} · ${u.rating}` }),
        ]),
      ]),
      el('button', {
        class: 'sb-logout', title: 'Выйти',
        onclick: async () => { await session.logout(); toast('Вы вышли из аккаунта', 'info'); },
      }, [icon('logout', 13)]),
    ]));
  }

  togglePin() {
    this.pinned = !this.pinned;
    settings.set(PIN_KEY, this.pinned);
    this.root.classList.toggle('pinned', this.pinned);
    const pin = this.root.querySelector('.sb-pin');
    clear(pin).append(icon(this.pinned ? 'anglesLeft' : 'anglesRight', 12));
    pin.title = this.pinned ? 'Открепить меню' : 'Закрепить меню';
  }

  setActive(path) {
    for (const [href, link] of this.links) {
      const active = href === '/' ? path === '/' : path.startsWith(href);
      link.classList.toggle('active', active);
    }
  }

  setBadge(count) {
    if (!this.badgeEl) return;
    this.badgeEl.textContent = String(count);
    this.badgeEl.classList.toggle('on', count > 0);
  }
}
